import { z } from 'zod/v4';

// ════════════════════════════════════════════════════════════════════════════
// `Flights` — the third pilot format (U19).
//
// GROUNDING (D28 — the population, enumerated and named).
//
// SOURCE OF RECORD, live: the 14 itinerary rows of the LIS→JFK 2026-10-15
// capture at
//   planning/research-layer/serpers/pipeline/delegation-harness/
//     flight-capture-1787075885219.json
// (searchapi `google_flights`, provider GO, one leg, 1 adult), minted by
// `flightRowMints` — a row record's payload IS the projection row plus the
// lane: `FlightItineraryRowPayload = {itinerary, lane}`
// (apps/bl/src/business-logic/orchestration/toolers/tools/research/
//  flight-search-projection.ts).
//
// Counted presence over the 14 captured itineraries, which is what decides
// each field one at a time. Unlike hotels' rows, the searchapi flights wire
// drops the KEY rather than writing null (carbonKg's single explicit null is
// the one exception), so "absent" below means the key was missing:
//
//   price, currency, type, totalDurationMinutes,
//   stopsCount, segments                          14/14, never null
//   layovers                                      12/14 — ABSENT on exactly
//                                                 the two stopsCount:0 rows.
//                                                 Null = nonstop, none named,
//                                                 never "unknown".
//   carbonKg                                      key on 14/14; on one row the
//                                                 LIVE value is NaN (the
//                                                 vendor sent no this_flight
//                                                 figure and the projection's
//                                                 division kept dividing) —
//                                                 the capture file spells it
//                                                 null because JSON cannot
//                                                 spell NaN. The builder
//                                                 normalizes non-finite to
//                                                 null; the block carries null.
//   carbonVsTypicalPct                            13/14 — absent exactly
//                                                 beside the NaN carbonKg
//
// And over the 26 segments those itineraries fly:
//
//   from/to {id, date, time}, durationMinutes,
//   airline, flightNumber, cabin                  26/26, never null
//   airplane, legroom                             25/26 — both absent on the
//                                                 same LEVEL-operated segment
//   wifi                                          20/26 — "free" | "for fee";
//                                                 localized free text, absence
//                                                 is unknown, never "no wifi"
//   oftenDelayed                                  5/26 — only `true` observed;
//                                                 the vendor flags, it never
//                                                 clears. Null is "not
//                                                 flagged", never a promise of
//                                                 punctuality.
//
// The journey block is the search's display context (§7.4), read from the
// SEARCH record's `originalArgs` — the tool's own validated input, whose zod
// defaults guarantee `attendants` with all four counts on every search that
// ever answered, and whose validate refuses a legless call outright. Every
// price on the card is a function of the party (the render states it as "the
// TOTAL for the whole party and itinerary"), so a price separated from its
// party is a number with no meaning — hotels' stay, one pillar over.
//
// NOT CARRIED, each for a stated reason rather than by omission:
//   resultKey, vendorToken, continuationKind   vendor continuation handles. A
//                                              persisted block is data, not a
//                                              handle (R26).
//   lane                                       the scraping middleman, which
//                                              this layer never names; the
//                                              envelope's `source.provider`
//                                              already carries GO.
//   sortBy, responseLanguageCode               request parameters. A render
//                                              may only claim what its input
//                                              proves (D22) — the served sort
//                                              can differ from the requested
//                                              one, and neither rides the row.
//   baggage, price filter, preferences         0 observed in the capture
//                                              (absent keys, no defaults); a
//                                              zero-population branch does not
//                                              ship.
//   coverage, priceInsights                    projection-envelope facts, not
//                                              row facts — no minted row
//                                              carries them.
//   airline_logo, typical/segment carbon,
//   layover airport names                      raw-wire material the
//                                              projection deliberately strips
//                                              or never carried; a
//                                              projection-only reader draws
//                                              mark-less rows by design.
//
// NO LINK FIELD EXISTS, and that is the census speaking: flights rows mint
// only the vendor-token address half — `address = {vendorToken}` on 14/14
// captured rows, no raw URL, no citation code — so there is no durable link
// to carry and no field an action link could occupy (§7.4, by construction).
//
// The one rename: the projection's `type` ("One way", a localized display
// string) lands here as `tripType`. `type` is a generic key inside a
// persisted JSON that models re-read; same clarity move as hotels' `image` →
// `imageUrl`.
//
// The un-captured lane, recorded: HasData's normalizer omits segment dates
// entirely. No HasData row has been captured, so this schema requires what
// the observed wire holds — a HasData row arriving at the builder fails the
// narrow and the block degrades to Links WHOLE (U18), which is the honest
// render for an unevidenced shape until its own capture justifies the field.
// ════════════════════════════════════════════════════════════════════════════

export const FLIGHTS_PAYLOAD_VERSION = 1;

/** One flown leg of the REQUEST, as the member asked it: origin, destination,
 *  departure date. Hour windows exist on the input contract but have zero
 *  captured population, so they do not ship. */
export const flightsLegSchema = z.object({
  from: z.string(),
  to: z.string(),
  date: z.string(),
});

/** The party the prices were quoted for — the whole itinerary's total buys
 *  exactly these travelers. All four counts are guaranteed by the tool's own
 *  input defaults on every search that answered. */
export const flightsPartySchema = z.object({
  adults: z.number().int(),
  children: z.number().int(),
  infantsInSeat: z.number().int(),
  infantsOnLap: z.number().int(),
});

/** The search's display context: the legs as asked, the party as priced. */
export const flightsJourneySchema = z.object({
  legs: z.array(flightsLegSchema).min(1),
  party: flightsPartySchema,
});

/** One end of a flown segment: airport code and two LOCAL clock fields with no
 *  timezone on the wire — durations are carried, never derived from these. */
export const flightsSegmentEndpointSchema = z.object({
  id: z.string(),
  date: z.string(),
  time: z.string(),
});

export const flightsSegmentSchema = z.object({
  from: flightsSegmentEndpointSchema,
  to: flightsSegmentEndpointSchema,
  durationMinutes: z.number().int(),
  /** A localized display name ("Aer Lingus"), not an IATA code. */
  airline: z.string(),
  /** Carrier code + number with a space ("EI 483"), verbatim. */
  flightNumber: z.string(),
  /** Per segment — a mixed-cabin itinerary has no single cabin. */
  cabin: z.string(),
  /** Free text with sub-model detail ("Boeing 737MAX 8 Passenger"). */
  airplane: z.string().nullable(),
  legroom: z.string().nullable(),
  /** Localized ("free" | "for fee" observed). Null is unknown, not "no wifi". */
  wifi: z.string().nullable(),
  /** Only `true` is ever sent. Null is "not flagged", never punctuality. */
  oftenDelayed: z.boolean().nullable(),
});

export const flightsRowSchema = z.object({
  /** The TOTAL for the whole party and the whole itinerary — never per person
   *  or per leg. */
  price: z.number(),
  currency: z.string(),
  /** The projection's `type` — a localized display string ("One way"). */
  tripType: z.string(),
  /** The only honest measure of trip length; the two local clock faces are
   *  never subtracted. */
  totalDurationMinutes: z.number().int(),
  stopsCount: z.number().int(),
  /** Null on a nonstop row — the wire drops the key rather than sending []. */
  layovers: z
    .array(z.object({ id: z.string(), durationMinutes: z.number().int() }))
    .nullable(),
  segments: z.array(flightsSegmentSchema).min(1),
  /** Null where the vendor published no figure — one captured row genuinely
   *  lacked one (a live NaN at the projection, normalized here). */
  carbonKg: z.number().nullable(),
  /** Signed; +13 = 13% worse than typical for this route. Null exactly where
   *  carbonKg is. */
  carbonVsTypicalPct: z.number().nullable(),
});

export const flightsPayloadSchema = z.object({
  journey: flightsJourneySchema,
  rows: z.array(flightsRowSchema).min(1),
});

export type FlightsLeg = z.infer<typeof flightsLegSchema>;
export type FlightsParty = z.infer<typeof flightsPartySchema>;
export type FlightsJourney = z.infer<typeof flightsJourneySchema>;
export type FlightsSegmentEndpoint = z.infer<typeof flightsSegmentEndpointSchema>;
export type FlightsSegment = z.infer<typeof flightsSegmentSchema>;
export type FlightsRow = z.infer<typeof flightsRowSchema>;
export type FlightsPayload = z.infer<typeof flightsPayloadSchema>;
